import { VStack } from "@repo/components/src/ui/vstack";
import { TaxProfileFieldKindEnum } from "@repo/graphql/gql/graphql";
import { ComponentProps, Fragment } from "react";

import AddressField from "./AddressField";
import BooleanField from "./BooleanField";
import { booleanFormName, textFormName } from "./fields";
import IdentifierField from "./IdentifierField";
import RegimeField from "./RegimeField";
import TextField from "./TextField";
import type { TaxProfileFieldData, TaxProfilePerspective } from "./types";
import type { TaxProfileFormState } from "./useTaxProfileForm";

type Props = {
  form: TaxProfileFormState;
  countryCode: string;
  perspective: TaxProfilePerspective;
  size: ComponentProps<typeof TextField>["size"];
};

/**
 * The form the country's policy describes: one component per field kind, in
 * the order the backend lists them. Nothing here depends on the country.
 */
export default function TaxProfileFields({
  form,
  countryCode,
  perspective,
  size,
}: Props) {
  const renderField = (field: TaxProfileFieldData) => {
    switch (field.kind) {
      case TaxProfileFieldKindEnum.Identifier:
        return (
          <IdentifierField
            field={field}
            form={form}
            countryCode={countryCode}
            size={size}
          />
        );
      case TaxProfileFieldKindEnum.Regime:
        return (
          <RegimeField field={field} form={form} perspective={perspective} />
        );
      case TaxProfileFieldKindEnum.Text:
      case TaxProfileFieldKindEnum.Email: {
        const name = textFormName(field.key);
        return name ? (
          <TextField field={field} name={name} form={form} size={size} />
        ) : null;
      }
      case TaxProfileFieldKindEnum.Address:
        return (
          <AddressField
            field={field}
            form={form}
            countryCode={countryCode}
            size={size}
          />
        );
      case TaxProfileFieldKindEnum.Boolean: {
        const name = booleanFormName(field.key);
        return name ? (
          <BooleanField field={field} name={name} form={form} size={size} />
        ) : null;
      }
    }
  };

  return (
    <VStack space="lg">
      {form.fields.map((field) => (
        <Fragment key={field.key}>{renderField(field)}</Fragment>
      ))}
    </VStack>
  );
}
